import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, storage } from '../../firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';
import PhotoCropModal from '../../components/ui/PhotoCropModal';
import { IconShieldCheck, IconCheck, IconCamera } from '@tabler/icons-react';

const STEPS = ['Your photo', 'Your details', 'Ready'];

export default function OnboardingManager() {
  const navigate = useNavigate();
  const { user, userProfile, orgId, refreshProfile } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [showCropModal, setShowCropModal] = useState(false);
  const [photoURL, setPhotoURL] = useState(null);
  const [jobTitle, setJobTitle] = useState('');
  const [phone, setPhone] = useState('');

  const resolvedOrgId = orgId || userProfile?.orgId;

  const handlePhotoSave = async (blob) => {
    try {
      const storageRef = ref(storage, `organisations/${resolvedOrgId}/avatars/${user.uid}`);
      await uploadBytes(storageRef, blob);
      const url = await getDownloadURL(storageRef);
      await updateDoc(
        doc(db, 'organisations', resolvedOrgId, 'users', user.uid),
        { photoURL: url, updatedAt: serverTimestamp() }
      );
      setPhotoURL(url);
      await refreshProfile();
    } catch (err) {
      console.error(err);
    }
  };

  const handleNext = async () => {
    setError('');

    if (step === 1) {
      setSaving(true);
      try {
        await updateDoc(
          doc(db, 'organisations', resolvedOrgId, 'users', user.uid),
          {
            jobTitle: jobTitle.trim() || null,
            phone: phone.trim() || null,
            updatedAt: serverTimestamp(),
          }
        );
        await refreshProfile();
      } catch (err) {
        console.error(err);
        setError('Failed to save — please try again');
        setSaving(false);
        return;
      }
      setSaving(false);
    }

    if (step === 2) {
      setSaving(true);
      try {
        await updateDoc(
          doc(db, 'organisations', resolvedOrgId, 'users', user.uid),
          { onboardingComplete: true, updatedAt: serverTimestamp() }
        );
        await refreshProfile();
        navigate('/site-dashboard');
      } catch (err) {
        console.error(err);
        setError('Failed to complete onboarding');
      }
      setSaving(false);
      return;
    }

    setStep(prev => prev + 1);
  };

  const initials = userProfile?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--color-bg-secondary)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{ width: '100%', maxWidth: 440 }}>

        <div style={{
          background: 'var(--color-brand)',
          borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0',
          padding: '24px 28px 20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{
              width: 36,
              height: 36,
              background: 'rgba(255,255,255,0.2)',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <IconShieldCheck size={20} color="#fff" stroke={1.5} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 400, color: '#fff' }}>Agriware Ops</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>Manager setup</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {STEPS.map((s, i) => (
              <div key={i} style={{ flex: 1 }}>
                <div style={{
                  height: 2,
                  borderRadius: 1,
                  background: i <= step ? '#fff' : 'rgba(255,255,255,0.3)',
                  marginBottom: 4,
                }} />
                <div style={{
                  fontSize: 9,
                  color: i === step ? '#fff' : 'rgba(255,255,255,0.5)',
                  letterSpacing: '0.04em',
                }}>
                  {s.toUpperCase()}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{
          background: 'var(--color-bg-primary)',
          borderRadius: '0 0 var(--radius-xl) var(--radius-xl)',
          border: '0.5px solid var(--color-border-light)',
          borderTop: 'none',
          padding: '28px',
        }}>

          {error && (
            <div style={{
              background: 'var(--color-danger-light)',
              border: '0.5px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              padding: '10px 14px',
              fontSize: 13,
              color: 'var(--color-danger)',
              marginBottom: 16,
            }}>
              {error}
            </div>
          )}

          {step === 0 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 400, marginBottom: 6 }}>
                Add your profile photo
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 24 }}>
                Your team will see this across the platform
              </div>
              <div
                onClick={() => setShowCropModal(true)}
                style={{
                  width: 90,
                  height: 90,
                  borderRadius: '50%',
                  background: photoURL ? 'transparent' : '#FDF0E8',
                  backgroundImage: photoURL ? `url(${photoURL})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  cursor: 'pointer',
                  fontSize: 28,
                  color: '#993C1D',
                  border: '2px solid transparent',
                  transition: 'border-color 0.15s',
                  position: 'relative',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--color-brand)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}
              >
                {!photoURL && initials}
                {photoURL && (
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    background: 'var(--color-brand)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <IconCamera size={13} color="#fff" stroke={1.5} />
                  </div>
                )}
              </div>
              <div
                onClick={() => setShowCropModal(true)}
                style={{ fontSize: 13, color: 'var(--color-brand)', cursor: 'pointer', marginBottom: 8 }}
              >
                {photoURL ? 'Change photo' : 'Upload photo'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                You can skip this and add it later
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <div style={{ fontSize: 18, fontWeight: 400, marginBottom: 6 }}>
                Your details
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 20 }}>
                Tell your team a bit about yourself
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                  Job title
                </label>
                <input
                  type="text"
                  placeholder="e.g. Farm Manager"
                  value={jobTitle}
                  onChange={e => setJobTitle(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                  Phone number
                </label>
                <input
                  type="tel"
                  placeholder="+44 7700 900 000"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'var(--color-success-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <IconCheck size={28} color="var(--color-success)" stroke={1.5} />
              </div>
              <div style={{ fontSize: 20, fontWeight: 400, marginBottom: 8 }}>
                You're all set
              </div>
              <div style={{
                fontSize: 13,
                color: 'var(--color-text-tertiary)',
                lineHeight: 1.6,
              }}>
                Welcome to Agriware Ops, {userProfile?.name?.split(' ')[0]}. Head to your dashboard to get started.
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
            {step > 0 && step < 2 && (
              <button
                onClick={() => setStep(prev => prev - 1)}
                style={{
                  flex: 1,
                  background: 'transparent',
                  color: 'var(--color-text-secondary)',
                  border: '0.5px solid var(--color-border-medium)',
                  borderRadius: 'var(--radius-md)',
                  padding: '10px',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={saving}
              style={{
                flex: 2,
                background: step === 2 ? 'var(--color-success)' : 'var(--color-brand)',
                color: '#fff',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                padding: '10px',
                fontSize: 13,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? 'Saving...' : step === 2 ? 'Go to dashboard' : 'Continue'}
            </button>
          </div>
        </div>
      </div>

      {showCropModal && (
        <PhotoCropModal
          onSave={handlePhotoSave}
          onClose={() => setShowCropModal(false)}
        />
      )}
    </div>
  );
}